import { useCallback } from 'react';
import Link from 'next/link';
import type { ToastAlertProps } from '@frameio/vapor';
import { Button, ToastAlert, useToast } from '@frameio/vapor';
import { getLink } from '../../../routing/getLink';
import type { FolderGetLinkParams } from '../../../routing/pages/folder';
import type { TrashGetLinkParams } from '../../../routing/pages/trash';
import TEXT from './generated/TEXT/useLoadingToast';
import { useCheckIsDestinationProjectRoot } from './useCheckIsDestinationProjectRoot';

const DURATION = 4000;

TEXT.DEFINE({
  /** Toast action button label to navigate to the project after an operation */
  goToProject: 'Go to project',
  /** Toast action button label to navigate to the target folder after an operation */
  goToFolder: 'Go to folder',
  /** Toast action button label to view the trash after moving items */
  gotToTrash: 'View',
});

/**
 * Returns functions to render vapor ToastAlerts commonly used for resource
 * operartions. These toasts persist as a loading state until the operation
 * succeeds or fails, then notifies the user of the outcome.
 *
 * @returns fns An object with callback functions to manage loading toasts
 * @returns fns.addToast Adds a new loading toast
 * @returns fns.updateAndHideToast Updates an existing loading toast, then removes
 *                                 it after a delay
 */
export function useLoadingToast() {
  const { addToast, updateToast } = useToast();

  // Extract TEXT properties to avoid calling hooks inside useCallback
  const textGoToProject = TEXT.goToProject;
  const textGoToFolder = TEXT.goToFolder;
  const textGotToTrash = TEXT.gotToTrash;

  /**
   * Adds a ToastAlert to the toast stack. Sets isLoading to true and uses
   * variant="neutral".
   *
   * @param title The title of the new toast
   * @returns The new toast's id used for updating
   */
  const addToastCallback = useCallback(
    (title: string): string => {
      return addToast(
        (closeToast, toastCloseLabel) => (
          <ToastAlert
            closeLabel={toastCloseLabel}
            isLoading={true}
            onRequestClose={closeToast}
            title={title}
            variant="neutral"
          />
        ),
        {
          duration: null,
        }
      );
    },
    [addToast]
  );

  const checkIsDestinationRoot = useCheckIsDestinationProjectRoot();
  /**
   * Updates an existing loading toast given a toastId, then hides it after a delay.
   * Providing a destination will result in a "Go to folder" button being rendered
   * in the toast.
   *
   * @param toastId The toast's id to update
   * @param toastProps The new props to use for the toast
   * @param destination An optional MoveTarget to display a "Go to" link for
   * @param duration Override the auto-dismiss delay in ms. Pass `null` to keep
   *                 the toast visible until the user clicks Dismiss (WCAG 2.2.1).
   */
  const updateAndHideToast = useCallback(
    (
      toastId: string,
      toastProps: ToastAlertProps,
      destination?: FolderGetLinkParams | TrashGetLinkParams,
      duration: number | null = DURATION
    ) => {
      const props: ToastAlertProps = {
        ...toastProps,
        isLoading: false,
      };
      let href = '';
      if (destination) {
        href = getLink(destination);
      }

      const isDestinationRoot = checkIsDestinationRoot(destination);

      let goToText = isDestinationRoot ? textGoToProject : textGoToFolder;
      if (destination?.page === 'trash') {
        goToText = textGotToTrash;
      }

      updateToast(
        toastId,
        (closeToast, toastCloseLabel) => (
          <ToastAlert
            {...props}
            // need to provide adornmentAfter here so we have access to dismiss()
            adornmentAfter={
              props.adornmentAfter ||
              (href && (
                <Link href={href}>
                  <Button
                    data-testid="toast-alert--cta"
                    onClick={closeToast}
                    onKeyDown={(event: React.KeyboardEvent) => {
                      if (event.key === 'Enter') closeToast();
                    }}
                    variant="link"
                  >
                    {goToText}
                  </Button>
                </Link>
              ))
            }
            closeLabel={toastCloseLabel}
            onRequestClose={closeToast}
          />
        ),
        { duration }
      );
    },
    [
      updateToast,
      textGoToFolder,
      textGotToTrash,
      textGoToProject,
      checkIsDestinationRoot,
    ]
  );

  return {
    addToast: addToastCallback,
    updateAndHideToast,
  };
}
